import { type Dispatch, type SetStateAction } from "react";

import { EnrichmentStevieCallItem } from "./EnrichmentStevieCallItem";
import { Column, ErrorText, LoadingIndicator, Text } from "@/components";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useGetVoiceAIOutreaches } from "@/hooks/queries/useGetVoiceAIOutreaches";
import type { EnrichmentCandidacy } from "./types";

export const StevieCallsPreviewDialog = ({
  candidacy,
  onOpenChange,
  open,
  selectedStevieCallIds,
  setSelectedStevieCallIds,
}: {
  candidacy: EnrichmentCandidacy;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedStevieCallIds: Set<string>;
  setSelectedStevieCallIds: Dispatch<SetStateAction<Set<string>>>;
}) => {
  /* Database CRUD Operations */
  // Read
  const outreachesQuery = useGetVoiceAIOutreaches({
    currentCandidacyId: candidacy.candidacyId,
  });

  /* Handlers */
  const handleToggleCallId = (outreachId: string) => {
    setSelectedStevieCallIds((prev) => {
      const next = new Set(prev);
      if (next.has(outreachId)) next.delete(outreachId);
      else next.add(outreachId);
      return next;
    });
  };

  /* Render */
  const renderBody = () => {
    if (outreachesQuery.isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (outreachesQuery.isError) {
      return <ErrorText errorMessage="Error retrieving Stevie calls." />;
    }
    if (outreachesQuery.data.length === 0) {
      return (
        <Text variant="muted">
          No Stevie calls recorded for this candidate.
        </Text>
      );
    }

    return (
      <Column className="gap-2">
        {outreachesQuery.data.map((outreach) => (
          <EnrichmentStevieCallItem
            key={outreach.id}
            candidacy={candidacy}
            isSelected={selectedStevieCallIds.has(outreach.id)}
            onToggle={() => {
              handleToggleCallId(outreach.id);
            }}
            voiceAIOutreach={outreach}
          />
        ))}
      </Column>
    );
  };

  const render = () => {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stevie calls</DialogTitle>
          </DialogHeader>
          {renderBody()}
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
